import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalWrapper, PromptForm } from "@/shared/ui";
import { useIsCoarsePointer } from "@/shared/hooks";
import type { useEgovMobileSigning } from "../hooks/useEgovMobileSigning";
import EgovMobileQrPanel from "./EgovMobileQrPanel";

interface Props {
  mobile: ReturnType<typeof useEgovMobileSigning>;
  onClose: () => void;
  title?: string;
}

/**
 * Closing the modal never cancels the operation — the user may have already
 * signed on the phone. Only the explicit cancel button ends the session.
 *
 * Visibility belongs to the caller: a session outlives the dialog, so the
 * dialog must not be able to hide itself in a way nothing can undo.
 */
export default function EgovMobileSigningModal({ mobile, onClose, title }: Props) {
  const { t } = useTranslation("Common");
  const isCoarsePointer = useIsCoarsePointer();
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  function handleConfirmCancel() {
    setShowConfirmCancel(false);
    mobile.handleCancel();
  }

  return (
    <>
      {showConfirmCancel && (
        <PromptForm
          variant="warning"
          title={t("signing.egov.cancelConfirmTitle")}
          text={t("signing.egov.cancelConfirmText")}
          confirmText={t("signing.egov.cancelConfirmButton")}
          cancelText={t("signing.egov.cancelConfirmBack")}
          isLoading={mobile.isCancelling}
          onConfirm={handleConfirmCancel}
          onClose={() => setShowConfirmCancel(false)}
          namespace="Common"
        />
      )}

      <ModalWrapper
        onClose={onClose}
        width="w-lg"
        isCloseDisabled={mobile.isFinalizing}
        closeDisabledHint={t("signing.finalizing.closeDisabledHint")}>
        <div className="flex flex-col gap-5">
          <h4 className="text-display-2xs content-base-primary">
            {title
              || t(
                mobile.isBusinessLink
                  ? "signing.egov.modalTitleBusiness"
                  : "signing.egov.modalTitlePersonal",
              )}
          </h4>
          <EgovMobileQrPanel
            qrCode={mobile.qrCode}
            primaryLink={mobile.primaryLink}
            secondaryLink={mobile.secondaryLink}
            isBusinessLink={mobile.isBusinessLink}
            expireAt={mobile.expireAt}
            now={mobile.now}
            isCoarsePointer={isCoarsePointer}
            isExpired={mobile.isExpired}
            isCancelling={mobile.isCancelling}
            error={mobile.error}
            isPending={mobile.launchState === "pending"}
            isFinalizing={mobile.isFinalizing}
            canRetry={mobile.canRetry}
            onCancel={mobile.isFinalizing ? onClose : () => setShowConfirmCancel(true)}
            onRestart={mobile.handleRetry}
          />
        </div>
      </ModalWrapper>
    </>
  );
}
